/**
 * VariablePicker - Reusable hierarchical variable picker component
 * Salesforce-style drill-down for struct members and nested types
 * Used by: AssignmentNodeEditor, ConditionBuilder, DecisionEditorPanel, LoopEditorPanel
 */
import React, { useMemo, useState, useCallback } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { 
  Variable,
  Calculator,
  ChevronRight,
  ChevronLeft,
  Grid3X3,
  Layers,
  Box,
  Pointer,
  BookOpen,
  Search,
} from 'lucide-react';

// Resource type to icon mapping
const RESOURCE_ICONS = {
  variable: Variable,
  constant: Variable,
  formula: Calculator,
  array_1d: Grid3X3,
  array_2d: Layers,
  struct_instance: Box,
  struct_definition: Box,
  union_instance: Layers,
  union_type: Layers,
  pointer: Pointer,
  function_pointer: Pointer,
  dictionary: BookOpen,
  set: Grid3X3,
  list: Grid3X3,
};

// ADO #291 — pointer-aware picker entries. Helper lives in pointerPicker.js
// so unit tests can import it without dragging in Radix UI deps.
import { expandPointerEntries, cTypeMatches } from './pointerPicker';
export { expandPointerEntries };
// ADO #312 follow-up — gate `&x` / `*p` synthetic entries on the active
// language. Only C / C++ have these operators; Java / Python / JavaScript
// would surface nonsensical picks (`&length address of`) otherwise.
import useConfigStore from '@/stores/useConfigStore';

// Helper to find struct definition by name or ID
export const findStructDefinition = (structTypeRef, allResources = []) => {
  if (!structTypeRef) return null;
  // Normalise pointer / `struct_`/`struct ` prefixed refs (e.g. a pointer
  // member stores `struct_Node`; a pointsToType may be `struct Node*`) down
  // to the bare tag so it matches the struct_definition's name.
  const bare = String(structTypeRef)
    .replace(/\*+\s*$/, '')
    .replace(/^struct[_\s]+/i, '')
    .trim();
  return allResources.find(r => {
    const resType = (r.resourceType || r.type || '').toLowerCase();
    // ADO #406 — union_type resolves through the same path (a union instance's
    // structTypeRef points at its UNION_TYPE, which carries structMembers).
    if (resType !== 'struct_definition' && resType !== 'struct_type' && resType !== 'union_type') return false;
    return r.id === structTypeRef || r.name === structTypeRef || r.name === bare;
  });
};

// ADO #354 — pointer depth + pointee tag for a resource. A `pointer` resource
// is one level deep over its pointsToType; otherwise depth is the `*` count in
// dataType. `tag` is the bare struct name (`struct_Node`/`struct Node*` → Node).
const pointerInfo = (resource) => {
  const type = (resource?.resourceType || resource?.type || '').toLowerCase();
  const strip = (s) => String(s || '').replace(/\*+/g, '').replace(/^struct[_\s]+/i, '').trim();
  if (type === 'pointer') {
    const p = (resource?.pointsToType || '').trim();
    return { level: 1 + (p.match(/\*/g) || []).length, tag: strip(p) };
  }
  const dt = (resource?.dataType || '').trim();
  return { level: (dt.match(/\*/g) || []).length, tag: strip(dt) };
};

// True only for a SINGLE-level pointer to a struct (`struct Node*`). Double
// pointers (`struct Node**`) are excluded: they'd need `(*p)->m`, which the
// flat `->` drill can't express — so we don't offer a (wrong) `p->m` for them.
const isPointerToStruct = (resource, allResources) => {
  const { level, tag } = pointerInfo(resource);
  if (level !== 1 || !tag) return false;
  const def = findStructDefinition(tag, allResources);
  return !!(def && ((def.structMembers?.length > 0) || (def.members?.length > 0)));
};

// ADO #579 — a flow PARAMETER is mirrored into a resource of kind 'variable'
// whose dataType is the declared type (useFlowStore). So `struct Words list`
// and `char text[]` reached the picker as plain scalars: no member drill, no
// [index]. A parameter behaves as its type: its struct definition when the type
// names one (by value — `struct X*` keeps the pointer path), an array when the
// type has `[]`.
export const paramStructDef = (resource, allResources = []) => {
  const type = (resource?.resourceType || resource?.type || '').toLowerCase();
  if (type !== 'variable') return null;
  const dt = String(resource?.dataType || '').trim();
  if (!dt || /[*[]/.test(dt)) return null;
  const tag = dt.replace(/^(struct|union)\s+/i, '').trim();
  const def = findStructDefinition(tag, allResources);
  return def && ((def.structMembers?.length > 0) || (def.members?.length > 0)) ? def : null;
};
// ADO #579 — a struct field that is an array (`char name[20]`, `char word[64][40]`),
// as getStructMembers lists it: memberType 'array_1d' / 'array_2d', not a nested struct.
export const isArrayMember = (item) =>
  !!item?.parentId && /array/i.test(String(item.memberType || '')) && !item.isNestedStruct;

// A struct field that is an ARRAY OF STRUCTS (`struct CalcToken token[N]` inside
// `Formula`): the struct tag of its element, or null. The picker indexes it and
// drills into the element's members — `f.token[i].kind` — one level, as it does
// for a top-level array of structs (structArrayElem).
export const arrayMemberStructElem = (item, allResources = []) => {
  if (!isArrayMember(item)) return null;
  const raw = String(item.structTypeRef || item.arrayElementType || item.dataType || '').replace(/\[.*$/, '').trim();
  if (!/^(struct|union)[_\s]+/i.test(raw) && !item.structTypeRef) return null;
  const tag = raw.replace(/^(struct|union)[_\s]+/i, '').trim();
  const def = tag ? findStructDefinition(tag, allResources) : null;
  return def && ((def.structMembers?.length > 0) || (def.members?.length > 0)) ? tag : null;
};

const isArrayTypedVariable = (resource) => {
  const type = (resource?.resourceType || resource?.type || '').toLowerCase();
  return type === 'variable' && /\[\s*\w*\s*\]/.test(String(resource?.dataType || ''));
};

// Check if a resource type has members that can be drilled into
export const hasMembers = (resource, allResources = []) => {
  const type = (resource?.resourceType || resource?.type || '').toLowerCase();
  
  // ADO #406 — union_instance shares the struct member-access path.
  if (type === 'struct_instance' || type === 'union_instance') {
    const structTypeRef = resource.structTypeRef;
    if (structTypeRef) {
      const structDef = findStructDefinition(structTypeRef, allResources);
      if (structDef?.structMembers?.length > 0) return true;
      if (structDef?.members?.length > 0) return true;
    }
    if (resource.structMembers?.length > 0) return true;
    if (resource.members?.length > 0) return true;
  }

  if (type === 'struct_definition' || type === 'struct_type' || type === 'union_type') {
    if (resource.structMembers?.length > 0) return true;
    if (resource.members?.length > 0) return true;
  }
  
  if (type === 'dictionary' || type === 'dict' || type === 'object') return true;

  if (paramStructDef(resource, allResources)) return true; // ADO #579

  // NOTE: pointer-to-struct is NOT drillable via hasMembers (which gates the
  // "row click drills" path used by value structs). ADO #372 (P1b) handles
  // pointer drilling separately: the row body SELECTS the whole pointer
  // (`current = current->next`) and a dedicated chevron DRILLS to follow
  // `->` members and chains (`current->next->data`). See ItemRow / handleDrill.

  return false;
};

// ADO #382 — the element struct type of an array-of-structs, or null. Reads
// the legacy `structType` field, else a `struct_*` / `struct *` element type
// stored in `dataType` (what AddResourceModalV2/ArrayForm saves). Lets the
// picker drill `table[i].field` for a `struct Record table[100]`.
export const structArrayElem = (resource) => {
  const rt = (resource?.resourceType || resource?.type || '').toLowerCase();
  if (!(rt === 'array_1d' || rt === 'array' || rt === 'array_2d')) return null;
  if (resource.structType) return resource.structType;
  const dt = String(resource.dataType || resource.elementType || '');
  const m = dt.match(/^struct[_\s]+(.+?)\s*$/i);
  return m ? m[1].trim() : null;
};

// ADO #432 — is this resource a string scalar (Python `str`, legacy `string`)?
// Strings are sequence-indexable/sliceable (`s[i]`, `s[1:4]`), so they earn the
// same index/slice field as arrays. char[]/char* are already arrays and handled
// by the `array`/`pointer` branches of hasIndexing.
export const isStringType = (resource) => {
  const rt = (resource?.resourceType || resource?.type || '').toLowerCase();
  if (rt === 'string') return true;
  // Only a plain scalar variable counts here — not arrays/pointers (already
  // indexable) or struct members declared elsewhere.
  if (rt !== 'variable' && rt !== '') return false;
  const dt = String(resource?.dataType || '').toLowerCase().trim();
  return dt === 'str' || dt === 'string';
};

// Check if a resource type has array-style indexing
export const hasIndexing = (resource) => {
  const type = (resource?.resourceType || resource?.type || '').toLowerCase();
  // ADO #431 — a dictionary is key-indexed (`ages["Ram"]`), so it gets the same
  // split-row + index/key field as an array (the key is quoted in codegen).
  // ADO #432 — a string is sequence-indexed/sliced (`s[i]`, `s[1:4]`).
  // ADO #433 — a tuple is sequence-indexed (`t[0]`), like a list.
  return type.includes('array') || type === 'list' || type === 'pointer'
    || isArrayTypedVariable(resource) // ADO #579 — `char text[]` parameter
    || type === 'dictionary' || type === 'dict' || type === 'tuple'
    || isStringType(resource);
};

// Get struct members from a resource
export const getStructMembers = (resource, allResources = []) => {
  const type = (resource?.resourceType || resource?.type || '').toLowerCase();
  const baseFullPath = resource.fullPath || resource.name;
  
  // `connector` is the access operator between parent and member: `.` for a
  // value struct, `->` for a pointer-to-struct (ADO #354 linked lists/trees).
  const mapMembers = (members, resourceId, resourceName, fullPathPrefix, connector = '.') => {
    return members.map(m => {
      let displayType = m.dataType || m.type || 'int';

      if (m.memberType === 'struct' && m.structTypeRef) {
        displayType = `struct ${m.structTypeRef}`;
      } else if (m.memberType === 'array' || m.memberType === 'array_1d') {
        // ADO #382 — handle array_1d (not just 'array') so a char[50] member
        // resolves to `char[50]` (a string), not `char` (single character),
        // which made the assignment editor reject "Rambabu" as a char literal.
        displayType = `${m.arrayElementType || m.dataType || 'int'}[${m.arraySize ?? m.size ?? ''}]`;
      } else if (m.memberType === 'array_2d') {
        displayType = `${m.arrayElementType || m.dataType || 'int'}[${m.rows ?? ''}][${m.cols ?? ''}]`;
      } else if (m.memberType === 'pointer') {
        displayType = `${m.pointsToType || m.dataType || 'int'}*`;
      }

      const memberName = m.name || m.id;
      const memberFullPath = `${fullPathPrefix}${connector}${memberName}`;

      return {
        id: `${resourceId}.${memberName}`,
        name: memberName,
        dataType: displayType,
        parentId: resourceId,
        parentName: resourceName,
        accessPath: `${connector}${memberName}`,
        fullPath: memberFullPath,
        memberType: m.memberType,
        structTypeRef: m.structTypeRef,
        isNestedStruct: m.memberType === 'struct' && !!m.structTypeRef
      };
    });
  };
  
  if (resource.structTypeRef && (resource.isNestedStruct || resource.memberType === 'struct')) {
    const structDef = findStructDefinition(resource.structTypeRef, allResources);
    if (structDef?.structMembers?.length > 0) {
      return mapMembers(structDef.structMembers, resource.id, resource.name, baseFullPath);
    }
    if (structDef?.members?.length > 0) {
      return mapMembers(structDef.members, resource.id, resource.name, baseFullPath);
    }
  }
  
  // ADO #579 — a `struct X` parameter drills into X's members.
  const paramDef = paramStructDef(resource, allResources);
  if (paramDef) {
    return mapMembers(paramDef.structMembers?.length ? paramDef.structMembers : paramDef.members,
      resource.id, resource.name, baseFullPath);
  }

  // ADO #406 — union_instance drills into members exactly like struct_instance.
  if (type === 'struct_instance' || type === 'union_instance') {
    const structTypeRef = resource.structTypeRef;
    if (structTypeRef) {
      const structDef = findStructDefinition(structTypeRef, allResources);
      if (structDef?.structMembers?.length > 0) {
        return mapMembers(structDef.structMembers, resource.id, resource.name, baseFullPath);
      }
      if (structDef?.members?.length > 0) {
        return mapMembers(structDef.members, resource.id, resource.name, baseFullPath);
      }
    }
    if (resource.structMembers?.length > 0) {
      return mapMembers(resource.structMembers, resource.id, resource.name, baseFullPath);
    }
    if (resource.members?.length > 0) {
      return mapMembers(resource.members, resource.id, resource.name, baseFullPath);
    }
  }

  if (type === 'struct_definition' || type === 'struct_type' || type === 'union_type') {
    if (resource.structMembers?.length > 0) {
      return mapMembers(resource.structMembers, resource.id, resource.name, baseFullPath);
    }
    if (resource.members?.length > 0) {
      return mapMembers(resource.members, resource.id, resource.name, baseFullPath);
    }
  }

  // ADO #354 — pointer to a struct: resolve the pointee definition and map its
  // members with the `->` connector (`newNode->data`, `head->next`, ...).
  if (isPointerToStruct(resource, allResources)) {
    const structDef = findStructDefinition(pointerInfo(resource).tag, allResources);
    const members = structDef?.structMembers?.length ? structDef.structMembers : structDef?.members;
    if (members?.length > 0) {
      return mapMembers(members, resource.id, resource.name, baseFullPath, '->');
    }
  }

  return [];
};

// ADO #372 (P1b) — cap pointer-chain drill depth (current->a->b->c->d) so a
// self-referential member (`next`) can't recurse forever in the picker.
const MAX_DRILL = 4;

// Single item row component
const ItemRow = ({ item, theme, onSelect, onDrill, getIcon, allResources, canDrillDeeper = true }) => {
  const Icon = getIcon(item);
  const isNestedStructMember = item.isNestedStruct || (item.memberType === 'struct' && item.structTypeRef);
  // ADO #291 — synthetic *a / &x entries never drill or index, even though
  // their _baseId would otherwise look struct-y when the base is a struct.
  const isSynthetic = !!item._synthetic;
  // ADO #372 (P1b) — a pointer-to-struct (a bare node pointer OR a pointer
  // member like `next`/`left`) is BOTH selectable whole (`cur = cur->next`)
  // AND drillable to follow its links (`cur->next->data`). The row body
  // selects; a separate chevron drills.
  const isPtrStruct = !isSynthetic && isPointerToStruct(item, allResources);
  const canDrillStruct = !isSynthetic && !isPtrStruct && ((hasMembers(item, allResources) && !item.parentId) || isNestedStructMember
    || !!arrayMemberStructElem(item, allResources));
  // ADO #408 — a plain pointer is selected WHOLE (not indexed), so don't render
  // it as an array (no `[i]` affordance); it reads as a normal scalar row.
  const isPlainPointer = (item.resourceType || item.type || '').toLowerCase() === 'pointer';
  const isArray = !isSynthetic && !isPtrStruct && !isPlainPointer && hasIndexing(item) && !item.parentId;
  // ADO #408 — an array of a struct type still drills into its members (single
  // row → onSelect drills). A PLAIN array (scalar elements) gets a struct-style
  // split row: name → whole array (`arr`), chevron → element (`arr[i]`).
  const arrayElemStruct = (!isSynthetic && !item.parentId) ? structArrayElem(item) : null;
  const isPlainArray = isArray && !arrayElemStruct;
  const syntheticHint =
    item._synthetic === 'deref' ? '(dereference)'
    : item._synthetic === 'addr' ? '(address of)'
    : null;

  const iconColor = (canDrillStruct || isPtrStruct)
    ? (theme.isDark ? 'text-violet-400' : 'text-violet-600')
    : isArray ? (theme.isDark ? 'text-emerald-400' : 'text-emerald-600')
    : isSynthetic ? (theme.isDark ? 'text-amber-400' : 'text-amber-600')
    : (theme.isDark ? 'text-blue-400' : 'text-blue-600');

  const rowBody = (
    <>
      <Icon className={`h-4 w-4 flex-shrink-0 ${iconColor}`} />
      {/* ADO #246 Phase B — displayName is prefixed (this./self.) on collisions. */}
      <span className="flex-1 truncate text-sm font-medium">{item.displayName || item.name}</span>
      {syntheticHint && (
        <span className={`text-[10px] italic ${theme.isDark ? 'text-amber-400/70' : 'text-amber-600/80'}`}>
          {syntheticHint}
        </span>
      )}
      <span className={`text-[11px] ${theme.isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
        {item.dataType || item.structType || ''}
      </span>
    </>
  );

  // Pointer-to-struct: split row — body selects the whole pointer, the chevron
  // drills to follow `->` members (depth-capped).
  if (isPtrStruct) {
    return (
      <div className={`w-full flex items-stretch rounded-lg transition-colors ${theme.isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}>
        <button
          onClick={() => onSelect(item)}
          className={`flex-1 min-w-0 flex items-center gap-2 px-3 py-2 text-left ${theme.isDark ? 'text-white' : 'text-slate-700'}`}
        >
          {rowBody}
        </button>
        {canDrillDeeper && (
          <button
            onClick={() => onDrill(item)}
            title="Follow members (→)"
            className={`px-2 flex items-center border-l ${theme.isDark ? 'border-white/5 text-violet-400 hover:bg-white/10' : 'border-slate-100 text-violet-600 hover:bg-slate-100'}`}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  }

  // Plain array: split row — body selects the whole array (`arr`, decays to a
  // pointer / for-each collection), the chevron picks an element (`arr[i]`,
  // index typed in the row). Beginners click the name for the array itself and
  // the arrow to reach into it — same model as struct members.
  if (isPlainArray) {
    return (
      <div className={`w-full flex items-stretch rounded-lg transition-colors ${theme.isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}>
        <button
          onClick={() => onSelect(item, { wholeArray: true })}
          className={`flex-1 min-w-0 flex items-center gap-2 px-3 py-2 text-left ${theme.isDark ? 'text-white' : 'text-slate-700'}`}
        >
          {rowBody}
        </button>
        <button
          onClick={() => onSelect(item)}
          title="Pick an element (arr[i])"
          className={`px-2 flex items-center border-l ${theme.isDark ? 'border-white/5 text-emerald-400 hover:bg-white/10' : 'border-slate-100 text-emerald-600 hover:bg-slate-100'}`}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => onSelect(item)}
      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-colors ${
        theme.isDark ? 'hover:bg-white/5 text-white' : 'hover:bg-slate-50 text-slate-700'
      }`}
    >
      {rowBody}
      {(canDrillStruct || isArray) && (
        <ChevronRight className={`h-4 w-4 ${theme.isDark ? 'text-zinc-500' : 'text-slate-400'}`} />
      )}
    </button>
  );
};

/**
 * VariablePicker Component
 * @param {Object} value - Current selection { variableId, variableName, fullPath, accessType, etc. }
 * @param {Function} onChange - Called with selection object when user picks a variable/member
 * @param {Array} allVariables - List of selectable variables (filtered from allResources)
 * @param {Array} allResources - All resources including struct definitions (for member lookup)
 * @param {Object} theme - Theme context { isDark }
 * @param {string} placeholder - Placeholder text when nothing selected
 * @param {string} width - Tailwind width class for the trigger button
 * @param {boolean} allowArrays - Whether to allow array type selection (default true)
 * @param {boolean} showOnlyArrays - Filter to show only array types (for for-each loops)
 */
const VariablePicker = ({
  value,
  onChange,
  allVariables,
  allResources,
  theme,
  placeholder = "Select variable...",
  width = "w-full",
  allowArrays = true,
  showOnlyArrays = false,
  // ADO #246 Phase B — when provided, render three labelled sections
  // (Instance / Parameters / Local) instead of the default type-based
  // groups. Items must already have `_group` and `displayName` set.
  scopedGroups = null,
  // ADO #354 — when the slot has a known expected type (e.g. an assignment
  // target's type), show a "Suggested" group of type-matching picks and
  // collapse the rest under "More options". Null → no type-aware split.
  expectedType = null,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [drillPath, setDrillPath] = useState([]);
  const [showMore, setShowMore] = useState(false);

  // ADO #312 follow-up — `&x` / `*p` synthetic entries are C / C++ only.
  // Java / Python / JavaScript don't have these operators, so the chips
  // would be nonsensical there.
  const language = useConfigStore((s) => s.language);
  const pointerAware = language === 'c' || language === 'cpp';

  // ADO #354 / #372 — expand a resource list into bare entries + `*p`/`&x`
  // pointer synthetics. Pointer-to-struct members are reached by DRILLING the
  // pointer (chevron) now, not via flat `ptr->member` siblings — which also
  // enables chains like `cur->next->data`.
  const expandAll = useCallback((xs) => {
    if (!pointerAware) return xs || [];
    return expandPointerEntries(xs);
  }, [pointerAware]);

  // Filter variables if showOnlyArrays is true. ADO #291 — also expand
  // each scalar/pointer to include its synthetic dereference / address-of
  // entry. Array-only mode skips expansion (no &arr or *arr makes sense
  // for a forEach collection picker).
  const filteredVariables = useMemo(() => {
    if (showOnlyArrays) {
      return allVariables.filter(v => {
        const type = (v.resourceType || v.type || '').toLowerCase();
        return type.includes('array') || type === 'list';
      });
    }
    return expandAll(allVariables);
  }, [allVariables, showOnlyArrays, expandAll]);
  
  // Get current level items
  const currentItems = useMemo(() => {
    if (drillPath.length === 0) {
      return filteredVariables;
    } else {
      const parent = drillPath[drillPath.length - 1];
      return getStructMembers(parent, allResources);
    }
  }, [drillPath, filteredVariables, allResources]);
  
  // Filter by search
  const searchFilteredItems = useMemo(() => {
    if (!search) return currentItems;
    const s = search.toLowerCase();
    return currentItems.filter(v =>
      v.name?.toLowerCase().includes(s) ||
      (v.dataType || '').toLowerCase().includes(s)
    );
  }, [currentItems, search]);

  // ADO #354 — type-aware split. When the slot has an expected type and we're
  // not drilling, flatten the candidate list (scoped sections, else the default
  // expanded list), search-filter it, and split into type-matching "Suggested"
  // vs the rest. Returns null (→ normal rendering) when nothing matches, so the
  // list is never reduced to an empty/wrong set.
  const typeAware = useMemo(() => {
    if (!expectedType || drillPath.length > 0) return null;
    const source = scopedGroups
      ? expandAll([
          ...(scopedGroups.instance || []),
          ...(scopedGroups.parameters || []),
          ...(scopedGroups.local || []),
        ])
      : filteredVariables;
    const s = search.toLowerCase();
    const match = (it) => !s
      || (it.displayName || it.name || '').toLowerCase().includes(s)
      || (it.dataType || '').toLowerCase().includes(s);
    const items = source.filter(match);
    const suggested = items.filter(it => cTypeMatches(it.dataType, expectedType));
    if (suggested.length === 0) return null;
    const more = items.filter(it => !cTypeMatches(it.dataType, expectedType));
    return { suggested, more };
  }, [expectedType, drillPath, scopedGroups, filteredVariables, expandAll, search]);
  
  // Group items by type (only at root level)
  const groupedItems = useMemo(() => {
    if (drillPath.length > 0) return { members: searchFilteredItems };
    
    const groups = {
      variables: [],
      arrays: [],
      structs: [],
      other: []
    };
    
    searchFilteredItems.forEach(v => {
      const type = (v.resourceType || v.type || '').toLowerCase();
      if (type.includes('array') || type === 'list') {
        groups.arrays.push(v);
      } else if (type.includes('struct') || type.includes('class') || type === 'object') {
        groups.structs.push(v);
      } else if (type === 'variable' || type === 'constant' || type === 'formula' || type === 'macro' || type === 'enum_instance') {
        // ADO #396 — a macro reads as a constant-like scalar value.
        // ADO #409 — an enum instance reads as a scalar (named-integer) value.
        groups.variables.push(v);
      } else if (type !== 'struct_definition' && type !== 'enum_type') {
        groups.other.push(v);
      }
    });
    
    return groups;
  }, [searchFilteredItems, drillPath]);
  
  // Handle item selection
  const handleSelect = (item, opts = {}) => {
    const isNestedStructMember = item.isNestedStruct || (item.memberType === 'struct' && item.structTypeRef);
    // ADO #408 — a plain pointer (int*/float*, NOT pointer-to-struct) is
    // assignable as a WHOLE (`p = arr`, `p++`). hasIndexing() is true for
    // pointers, which would otherwise force `p[]` array mode — so it's excluded
    // from the array branch below and selected as a scalar instead. Indexed /
    // deref access stays available via the `*p` synthetic entry or Expression.
    const itemIsPlainPointer = (item.resourceType || item.type || '').toLowerCase() === 'pointer'
      && !isPointerToStruct(item, allResources);

    // ADO #408 — split-row "whole array" pick (the row body, not the chevron).
    // Selects the bare array name (`arr`) which decays to a pointer — used for
    // `p = arr` and for-each collections. The chevron path keeps the indexed
    // `arr[i]` behaviour below. Mirrors the struct row: name → the thing,
    // chevron → go deeper.
    if (opts.wholeArray) {
      const emitName = item.displayName || item.name;
      // The array name decays to a pointer to its element type, so report the
      // EFFECTIVE type as `int*` (not the element `int`) — this drives the
      // assignment type hint (`int* → int*`) and pointer compatibility check.
      const elemType = item.dataType || item.elementType || 'int';
      const decayedType = /\*/.test(elemType) ? elemType : `${elemType}*`;
      onChange({
        variableId: item.id,
        variableName: emitName,
        accessType: 'scalar',
        accessPath: '',
        fullPath: emitName,
        dataType: decayedType,
      });
      setOpen(false);
      setDrillPath([]);
      setSearch('');
      return;
    }

    // ADO #408 — a plain pointer is selected WHOLE (`p = …`, `p++`). Report its
    // effective pointer type (`int*`) so the type hint and compatibility check
    // are correct even when the resource stored only `pointsToType` (no `*` in
    // dataType). Pointer-to-struct is handled by its own branch below.
    if (itemIsPlainPointer && !item.parentId) {
      const emitName = item.displayName || item.name;
      const rawDt = item.dataType || '';
      const ptrType = /\*/.test(rawDt) ? rawDt : `${item.pointsToType || rawDt || 'int'}*`;
      onChange({
        variableId: item.id,
        variableName: emitName,
        accessType: 'scalar',
        accessPath: '',
        fullPath: emitName,
        dataType: ptrType,
      });
      setOpen(false);
      setDrillPath([]);
      setSearch('');
      return;
    }

    // ADO #291 — synthetic *a / &x entry. Emit the BASE resource id so
    // downstream type lookups (AssignmentRow.targetDataType etc.) keep
    // resolving to the real resource; the operator survives in fullPath.
    if (item._synthetic) {
      onChange({
        variableId: item._baseId,
        variableName: item._baseName,
        // `*a` / `&x` are scalars; `ptr->member` is a member access.
        accessType: item._synthetic === 'arrow' ? 'member' : 'scalar',
        accessPath: item.accessPath || '',
        memberName: item._synthetic === 'arrow' ? item.name : '',
        fullPath: item.fullPath || item.name, // '*a' / '&x' / 'ptr->member'
        // ADO #354 — resolved type of the picked value (member/deref type),
        // so a target's expected type drives the value picker's Suggested group.
        dataType: item.dataType,
      });
      setOpen(false);
      setDrillPath([]);
      setSearch('');
      return;
    }

    // ADO #354 — a single-level pointer-to-struct is a node reference; select
    // it whole (`current = ...`). Its `->members` are the flat entries above,
    // so we deliberately do NOT treat it as a drillable struct or an array.
    if (!item.parentId && isPointerToStruct(item, allResources)) {
      const emitName = item.displayName || item.name;
      onChange({
        variableId: item.id,
        variableName: emitName,
        accessType: 'scalar',
        accessPath: '',
        fullPath: emitName,
        // Emit the pointer's type so the assignment type-check resolves the
        // target correctly (e.g. `head : struct Node*`). Without this the
        // target defaulted to `int`, flagging `head = head->next` as a
        // false type error when `head` is a parameter not in allVariables.
        dataType: item.dataType,
      });
      setOpen(false);
      setDrillPath([]);
      setSearch('');
      return;
    }

    // Struct arrays (array of a struct type): drill into struct members first
    // so the user can pick e.g. table[i].name instead of just table[]. ADO #382 —
    // the element struct comes from `structType` OR a `struct_*` dataType.
    const structElem = !item.parentId ? structArrayElem(item) : null;
    const isStructArray = hasIndexing(item) && !item.parentId && !!structElem;

    if (isStructArray) {
      // Find the struct definition and drill into its members
      const structDef = findStructDefinition(structElem, allResources);
      if (structDef) {
        const virtualItem = {
          ...item,
          id: item.id,
          name: item.name,
          fullPath: `${item.name}[]`,
          structTypeRef: structElem,
          resourceType: 'struct_instance',
          isArrayOfStruct: true,
        };
        setDrillPath([...drillPath, virtualItem]);
        setSearch('');
        return;
      }
    }

    // An array-of-structs FIELD (`f.token`): index it and drill into the
    // element's members, so the pick is `f.token[].kind` — codegen splices
    // the row's index into the `[]` (applyArrayIndices) → `f.token[i].kind`.
    const memberElem = item.parentId ? arrayMemberStructElem(item, allResources) : null;
    if (memberElem) {
      const virtualItem = {
        ...item,
        // Members keep the BASE resource as their parent, as for `table[].name`.
        id: item.parentId,
        name: item.name,
        fullPath: `${item.fullPath}[]`,
        structTypeRef: memberElem,
        resourceType: 'struct_instance',
        memberType: 'struct_array_element',
        isArrayOfStruct: true,
      };
      setDrillPath([...drillPath, virtualItem]);
      setSearch('');
      return;
    }

    if ((hasMembers(item, allResources) && !item.parentId) || isNestedStructMember) {
      if (isNestedStructMember && item.structTypeRef) {
        const nestedStructDef = findStructDefinition(item.structTypeRef, allResources);
        if (nestedStructDef) {
          const virtualItem = {
            ...item,
            id: item.id,
            name: item.name,
            fullPath: item.fullPath,
            structTypeRef: item.structTypeRef,
            resourceType: 'struct_instance'
          };
          setDrillPath([...drillPath, virtualItem]);
          setSearch('');
          return;
        }
      }
      setDrillPath([...drillPath, item]);
      setSearch('');
    } else if (hasIndexing(item) && !item.parentId && allowArrays && !itemIsPlainPointer) {
      // ADO #246 — use displayName for prefixed instance refs so the
      // emitted variableName / fullPath include this./self.
      const emitName = item.displayName || item.name;
      onChange({
        variableId: item.id,
        variableName: emitName,
        accessType: 'array',
        accessPath: '',
        fullPath: `${emitName}[]`
      });
      setOpen(false);
      setDrillPath([]);
      setSearch('');
    } else if (item.parentId && allowArrays && isArrayMember(item)) {
      // ADO #579 — an array FIELD of a struct (`rec.name`, `list.word`) is
      // indexed like an array: `rec.name[i]`. The row's index field and codegen
      // already fill a trailing `[]` (AssignmentRow needsArrayIndex,
      // codeGenerator fillIndex); only the pick was missing.
      onChange({
        variableId: item.parentId,
        variableName: item.parentName,
        accessType: 'array',
        accessPath: `${item.accessPath || ''}[]`,
        memberName: item.name,
        fullPath: `${item.fullPath}[]`,
        dataType: item.dataType,
      });
      setOpen(false);
      setDrillPath([]);
      setSearch('');
    } else {
      const emitName = item.displayName || item.name;
      onChange({
        variableId: item.parentId || item.id,
        variableName: item.parentName || emitName,
        accessType: item.parentId ? 'member' : 'scalar',
        accessPath: item.accessPath || '',
        memberName: item.parentId ? item.name : '',
        fullPath: item.fullPath || emitName,
        // ADO #354 — resolved member/value type for the Suggested split.
        dataType: item.dataType,
      });
      setOpen(false);
      setDrillPath([]);
      setSearch('');
    }
  };
  
  const handleBack = () => {
    setDrillPath(drillPath.slice(0, -1));
    setSearch('');
  };

  // ADO #372 (P1b) — drill into a pointer-to-struct (bare node pointer or a
  // pointer member) to follow its `->` links. getStructMembers resolves the
  // pointee's members with the `->` connector, so chains build naturally
  // (`cur` → `cur->next` → `cur->next->data`).
  const handleDrill = (item) => {
    setDrillPath([...drillPath, item]);
    setSearch('');
  };
  const canDrillDeeper = drillPath.length < MAX_DRILL;
  
  const getItemIcon = (item) => {
    const type = (item.resourceType || item.type || 'variable').toLowerCase();
    return RESOURCE_ICONS[type] || Variable;
  };
  
  const breadcrumb = drillPath.map(p => p.name).join(' > ');
  const displayValue = value?.fullPath || placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="variable-picker-trigger"
          className={`flex items-center justify-between ${width} h-9 px-3 text-sm font-mono rounded-lg border transition-colors ${
            theme.isDark
              ? 'bg-zinc-800 border-zinc-700 text-emerald-400 hover:border-zinc-600'
              : 'bg-white border-slate-200 text-emerald-600 hover:border-slate-300'
          } ${!value?.fullPath && !value?.variableId ? 'text-opacity-50' : ''}`}
        >
          <span className="truncate">{displayValue}</span>
          <ChevronRight className="h-4 w-4 opacity-50 flex-shrink-0 ml-2" />
        </button>
      </PopoverTrigger>
      
      {/* Radix portals this to document.body, so it sits OUTSIDE whatever a
          tour has highlighted and driver's overlay swallows clicks on it. A
          tour must therefore highlight this element itself for the step where
          the student picks — see the retconfig steps in flowBuilderTour. */}
      <PopoverContent 
        data-testid="variable-picker-list"
        className={`w-80 p-0 ${theme.isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white'}`}
        align="start"
      >
        {/* Header */}
        <div className={`p-2 border-b ${theme.isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
          {drillPath.length > 0 && (
            <div className="flex items-center gap-2 mb-2">
              <button
                onClick={handleBack}
                className={`p-1.5 rounded-lg transition-colors ${
                  theme.isDark ? 'hover:bg-white/10 text-zinc-400' : 'hover:bg-slate-100 text-slate-500'
                }`}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className={`text-sm font-medium truncate ${theme.isDark ? 'text-white' : 'text-slate-700'}`}>
                {breadcrumb}
              </span>
            </div>
          )}
          
          <div className="relative">
            <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 ${
              theme.isDark ? 'text-zinc-500' : 'text-slate-400'
            }`} />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className={`pl-8 h-8 text-sm ${
                theme.isDark 
                  ? 'bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500'
                  : 'bg-slate-50 border-slate-200 placeholder:text-slate-400'
              }`}
            />
          </div>
        </div>
        
        {/* Items list */}
        <div className="max-h-64 overflow-y-auto p-1">
          {drillPath.length === 0 && typeAware ? (
            <>
              {/* ADO #354 — type-aware: Suggested (matches the slot's type) on
                  top, the rest collapsed under "More options". */}
              <div className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-wider ${theme.isDark ? 'text-violet-400/80' : 'text-violet-600'}`}>
                Suggested · {expectedType}
              </div>
              {typeAware.suggested.map(item => (
                <ItemRow key={`sug-${item.id}`} item={item} theme={theme} onSelect={handleSelect} onDrill={handleDrill} getIcon={getItemIcon} allResources={allResources} canDrillDeeper={canDrillDeeper} />
              ))}
              {typeAware.more.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={() => setShowMore(s => !s)}
                    className={`w-full text-left px-2 py-1.5 mt-1 text-[11px] ${theme.isDark ? 'text-violet-300 hover:bg-white/5' : 'text-violet-600 hover:bg-slate-50'}`}
                  >
                    {showMore ? '▾' : '▸'} More options ({typeAware.more.length})
                  </button>
                  {showMore && typeAware.more.map(item => (
                    <ItemRow key={`more-${item.id}`} item={item} theme={theme} onSelect={handleSelect} onDrill={handleDrill} getIcon={getItemIcon} allResources={allResources} canDrillDeeper={canDrillDeeper} />
                  ))}
                </>
              )}
            </>
          ) : drillPath.length === 0 && scopedGroups ? (
            <>
              {/* ADO #246 Phase B — Instance / Parameters / Local sections.
                  Searches still apply (search filters via parent's allVariables,
                  so scopedGroups is the pre-filtered three-way split). */}
              {(() => {
                const s = search.toLowerCase();
                const match = (it) => !s
                  || (it.displayName || it.name || '').toLowerCase().includes(s)
                  || (it.name || '').toLowerCase().includes(s)
                  || (it.dataType || '').toLowerCase().includes(s);
                // ADO #291 — expand pointer/scalar entries within each
                // scope section so *a / &x show up alongside their bases.
                // ADO #354 — expandAll also adds `ptr->member` entries.
                // ADO #312 follow-up — only on C / C++ (no `&` / `*` in
                // Java / Python / JavaScript).
                const expand = (xs) => expandAll(xs);
                const sections = [
                  { key: 'instance',   label: 'Instance',   items: expand(scopedGroups.instance   || []).filter(match) },
                  { key: 'parameters', label: 'Parameters', items: expand(scopedGroups.parameters || []).filter(match) },
                  { key: 'local',      label: 'Local',      items: expand(scopedGroups.local      || []).filter(match) },
                ];
                const empty = sections.every(sec => sec.items.length === 0);
                if (empty) {
                  return (
                    <div className={`px-3 py-6 text-center text-sm ${theme.isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                      {showOnlyArrays ? 'No arrays in scope' : 'No resources in scope'}
                    </div>
                  );
                }
                return sections.map(sec => sec.items.length > 0 && (
                  <div key={sec.key} className="mb-2">
                    <div className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-wider ${theme.isDark ? 'text-zinc-500' : 'text-slate-400'}`}>{sec.label}</div>
                    {sec.items.map(item => (
                      <ItemRow
                        key={`${sec.key}-${item.id}`}
                        item={item}
                        theme={theme}
                        onSelect={handleSelect}
                        onDrill={handleDrill}
                        getIcon={getItemIcon}
                        allResources={allResources}
                        canDrillDeeper={canDrillDeeper}
                      />
                    ))}
                  </div>
                ));
              })()}
            </>
          ) : drillPath.length === 0 ? (
            <>
              {groupedItems.variables?.length > 0 && (
                <div className="mb-2">
                  <div className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-wider ${
                    theme.isDark ? 'text-zinc-500' : 'text-slate-400'
                  }`}>Variables</div>
                  {groupedItems.variables.map(item => (
                    <ItemRow key={item.id} item={item} theme={theme} onSelect={handleSelect} onDrill={handleDrill} getIcon={getItemIcon} allResources={allResources} canDrillDeeper={canDrillDeeper} />
                  ))}
                </div>
              )}
              
              {groupedItems.arrays?.length > 0 && (
                <div className="mb-2">
                  <div className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-wider ${
                    theme.isDark ? 'text-zinc-500' : 'text-slate-400'
                  }`}>Arrays</div>
                  {groupedItems.arrays.map(item => (
                    <ItemRow key={item.id} item={item} theme={theme} onSelect={handleSelect} onDrill={handleDrill} getIcon={getItemIcon} allResources={allResources} canDrillDeeper={canDrillDeeper} />
                  ))}
                </div>
              )}
              
              {groupedItems.structs?.length > 0 && (
                <div className="mb-2">
                  <div className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-wider ${
                    theme.isDark ? 'text-zinc-500' : 'text-slate-400'
                  }`}>Structs / Objects</div>
                  {groupedItems.structs.map(item => (
                    <ItemRow key={item.id} item={item} theme={theme} onSelect={handleSelect} onDrill={handleDrill} getIcon={getItemIcon} allResources={allResources} canDrillDeeper={canDrillDeeper} />
                  ))}
                </div>
              )}
              
              {groupedItems.other?.length > 0 && (
                <div className="mb-2">
                  <div className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-wider ${
                    theme.isDark ? 'text-zinc-500' : 'text-slate-400'
                  }`}>Other</div>
                  {groupedItems.other.map(item => (
                    <ItemRow key={item.id} item={item} theme={theme} onSelect={handleSelect} onDrill={handleDrill} getIcon={getItemIcon} allResources={allResources} canDrillDeeper={canDrillDeeper} />
                  ))}
                </div>
              )}
              
              {Object.values(groupedItems).every(g => !g || g.length === 0) && (
                <div className={`px-3 py-6 text-center text-sm ${theme.isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                  {showOnlyArrays ? 'No arrays found' : 'No resources found'}
                </div>
              )}
            </>
          ) : (
            <div>
              <div className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-wider ${
                theme.isDark ? 'text-zinc-500' : 'text-slate-400'
              }`}>Members of {drillPath[drillPath.length - 1]?.name}</div>
              {searchFilteredItems.length > 0 ? (
                searchFilteredItems.map(item => (
                  <ItemRow key={item.id} item={item} theme={theme} onSelect={handleSelect} onDrill={handleDrill} getIcon={getItemIcon} allResources={allResources} canDrillDeeper={canDrillDeeper} />
                ))
              ) : (
                <div className={`px-3 py-6 text-center text-sm ${theme.isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                  No members defined for this struct
                </div>
              )}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default VariablePicker;
